import React, { Fragment, useEffect, useState } from 'react';
import { Button }from '@cmsgov/design-system';

import { FormattedMessage } from 'react-intl';

// Components

import InstructionalText from '../notifications/InstructionalText';
import Form from '../../../lib/wrappers/Form';
import ModalHeader from '../../common/headers/ModalHeader';
import UniversalInput from '../../../lib/wrappers/UniversalInput';

// Utils

import { normalizeNameOfPerson } from '../../../utils/normalize/normalizeNameOfPerson';
import { omit } from '../../../utils/miscellaneous/objectHelpers';

// Constants

import { INPUT_TYPES } from '../../../constants/inputs/inputTypes';
import { INSTRUCTIONAL_TEXT_STYLE_TYPES } from '../../../constants/common/styleTypes';
import { DROPDOWN_OPTIONS } from '../../../constants/ssap/hhmTypes/dropdownOptions';
import { VALIDATION_TYPES } from '../../../constants/inputs/validationTypes';
import { DEDUCTIONS_SOURCES, INCOME_SOURCES, FREQUENCY_OPTIONS_TYPES } from '../../../constants/ssap/hhmTypes/incomeSourcesList';
import { INCOME_TYPES, OTHER_INCOME_TYPES } from '../../../constants/ssap/hhmTypes/incomeAndExpenceTypes';
import { FREQUENCY_TYPES } from '../../../constants/ssap/hhmTypes/frequencyTypes';
import { VALIDATION_ERROR_MESSAGES } from '../../../constants/inputs/validationErrorMessages';




const AddIncomeOrDeductionSourceModal = props => {

    const {
        ALIMONY,
        JOB,
        OTHER,
        UNSPECIFIED
    } = INCOME_TYPES;

    const {
        OTHER_INCOME
    } = OTHER_INCOME_TYPES;

    const {
        DAILY,
        HOURLY,
        ONCE,
        YEARLY
    } = FREQUENCY_TYPES;

    const {
        MODAL: MODAL_STYLE_TYPE
    } = INSTRUCTIONAL_TEXT_STYLE_TYPES;

    let {
        actions: {
            changeData,
            hideModal
        },
        bottomAnchors: [
            anchor_1,
            anchor_2,
        ],
        boundAnchor,
        data,
        data: {
            coverageYear
        },
        environment: {
            exchangeName,
            stateCode
        },
        form,
        formName,
        householdMembers,
        hyperReview,
        methods,
        modal: {
            modalData: {
                source: {
                    amount = null,
                    frequency = null,
                    sourceName = null,
                    type = null,
                    subType = UNSPECIFIED,
                    relatedExpense = null,
                    cyclesPerFrequency = null,
                    tribalAmount = null,
                    incomeExpectedForWholeYear = null,
                    incomeStartDate = null,
                    incomeEndDate = null
                },
                index,
                parameter,
                pageToReturn,
                option = undefined
            }
        },
        modalHeaderProps
    } = props;

    const getDataInstructions = (t, source = 'source') => {
        return `instructional_text.${ source }.${ typeof t === 'string' ? t.toLowerCase() : 'null' }`;
    };

    const {
        hhmIndex: member,
        currentHhm: currentHouseholdMember
    } = props;

    const {
        name
    } = currentHouseholdMember;

    const [ localState, setLocalState ] = useState({
        amount,
        frequency,
        sourceName,
        type: option ? option : type,
        profit: amount > 0,
        subType,
        relatedExpense,
        cyclesPerFrequency,
        tribalAmount,
        incomeExpectedForWholeYear,
        incomeStartDate,
        incomeEndDate
    });

    const [ areChangesApplied, changeStatus ] = useState(false);

    const handleSaveClick = () => {

        if(!areChangesApplied && form[formName].isEligiblyForSave) {
            hideModal();
        }

        const updateData = () => {
            const newState = {
                ...data,
                ...(hyperReview ? { maxAchievedPage: pageToReturn } : {})
            };

            const dataForSave = omit(localState, ['profit']);

            if(index === null) {
                newState
                    .taxHousehold
                    .householdMember[member][parameter]
                    .push(dataForSave);
            } else {
                const { sourceName } = dataForSave;
                const applicantGuid = newState.taxHousehold.householdMember[member].applicantGuid;
                const oldSourceName = newState.taxHousehold.householdMember[member][parameter][index].sourceName;

                newState.taxHousehold.householdMember[member][parameter][index] = {
                    ...newState.taxHousehold.householdMember[member][parameter][index],
                    ...dataForSave
                };

                if(sourceName !== oldSourceName) {
                    newState
                        .taxHousehold
                        .householdMember = newState
                            .taxHousehold
                            .householdMember.map(hhm => {
                                return {
                                    ...hhm,
                                    employerSponsoredCoverage: hhm.employerSponsoredCoverage
                                        .reduce((coverages, coverage) => {
                                            if(coverage.applicantGuid === applicantGuid && coverage.companyName === oldSourceName) {
                                                return [ ...coverages, { ...coverage, companyName: sourceName } ];
                                            } else {
                                                return [ ...coverages, coverage ];
                                            }
                                        }, []),
                                    healthReimbursementArrangement: hhm.healthReimbursementArrangement
                                        .reduce((hras, hra) => {
                                            if(hra.applicantGuid === applicantGuid && hra.employerName === oldSourceName) {
                                                return [ ...hras, { ...hra, employerName: sourceName } ];
                                            } else {
                                                return [ ...hras, hra ];
                                            }
                                        }, []),
                                };
                            });
                }
            }

            changeData(newState);
        };

        methods[formName].onFormSubmitForValidationOnly([updateData]);
    };

    const valuesForWatch = householdMembers[member][parameter].length === 0 ?
        'no values' :
        householdMembers[member][parameter].map(value => Object.values(value).toString()).toString();

    useEffect(() => {
        if(valuesForWatch !== 'no values' && areChangesApplied) {
            changeStatus(false);
            methods[formName]
                .onFormSubmit(
                    pageToReturn,
                    [() => hideModal()],
                    null,
                    {
                        ...data,
                        ...(hyperReview ? { maxAchievedPage: pageToReturn } : {})
                    }
                );
        }
    }, [valuesForWatch]);

    const onFieldChange = newData => {

        const [ key, value ] = Object.entries(newData)[0];

        if(key === 'type' && value !== localState.type) {
            setLocalState({
                amount: null,
                frequency: null,
                sourceName: null,
                type: newData.type,
                profit: null,
                subType: value === OTHER ? localState.subType : UNSPECIFIED,
                relatedExpense: null,
                tribalAmount: null,
                incomeExpectedForWholeYear: null,
                incomeStartDate: null,
                incomeEndDate: null
            });
        } else if(key === 'subType' && value !== localState.subType) {
            setLocalState({
                amount: null,
                frequency: null,
                sourceName: null,
                type: localState.type,
                profit: null,
                subType: newData.subType,
                relatedExpense: null,
                tribalAmount: null,
                incomeExpectedForWholeYear: null,
                incomeStartDate: null,
                incomeEndDate: null
            });
        } else if (key === 'amount') {
            setLocalState(state => ({
                ...{
                    ...state,
                    profit: value > 0
                },
                ...newData
            }));
        } else if (key === 'profit') {
            setLocalState(state => {
                return {
                    ...{
                        ...state,
                        amount: value === null ? state.amount : (value ? 1 : -1) * Math.abs(state.amount)
                    },
                    ...newData
                };
            });
        } else if (key === 'cyclesPerFrequency') {
            setLocalState(state => {
                return {
                    ...state,
                    cyclesPerFrequency: Number(value)
                };
            });
        } else if (key === 'frequency') {
            setLocalState(state => {
                return {
                    ...state,
                    frequency: value,
                    cyclesPerFrequency: null,
                    incomeStartDate: (value === YEARLY || value === ONCE) ? null : state.incomeStartDate,
                    incomeEndDate: (value === YEARLY || value === ONCE) ? null : state.incomeEndDate
                };
            });
        } else if (key === 'incomeExpectedForWholeYear' && value === true) {
            setLocalState(state => {
                return {
                    ...state,
                    incomeExpectedForWholeYear: value,
                    incomeStartDate: null,
                    incomeEndDate: null
                };
            });
        } else {
            setLocalState(state => ({
                ...state,
                ...newData
            }));
        }
        areChangesApplied === false && changeStatus(true);
    };

    const getAdditionalInputByType = inputsData => {
        switch(inputsData.additionalInput.inputType) {
            case INPUT_TYPES.DROPDOWN:
                return getDropdownInput(inputsData);
            case INPUT_TYPES.MONEY_INPUT:
                return getMoneyInput(inputsData);
            case INPUT_TYPES.REGULAR_INPUT:
                return getRegularInput(inputsData);
            default:
                return <p>{'Input\'s been lost'}</p>;
        }
    };

    const getRegularInput = inputsData => {
        const {
            additionalInput: {
                additionalInputText,
                required,
                validationType
            }
        } = inputsData;

        const propName = 'sourceName';

        return (
            <UniversalInput
                label={{
                    text: additionalInputText
                }}
                inputData={{
                    isInputRequired: required,
                    type: INPUT_TYPES.REGULAR_INPUT,
                    name: propName,
                    value: localState[propName],
                    placeholder: additionalInputText.replace('label', 'placeholder'),
                    validationType,
                    currentData: { [propName]: localState[propName] },
                    fields: []
                }}
                inputActions={{
                    onFieldChange
                }}
            />
        );
    };

    const getMoneyInput = inputsData => {
        const {
            additionalInput: {
                additionalInputText,
                required,
                isOnlyPositiveAvailable,
            }
        } = inputsData;

        return (
            <UniversalInput
                label={{
                    text: additionalInputText,
                    labelClasses: ['tablet:grid-col-4']
                }}
                inputData={{
                    isInputRequired: required,
                    type: INPUT_TYPES.MONEY_INPUT,
                    name: 'relatedExpense',
                    value: localState.relatedExpense,
                    inputClasses: ['tablet:grid-col-6'],
                    isOnlyPositiveAvailable: !!isOnlyPositiveAvailable,
                    placeholder: additionalInputText.replace('label', 'placeholder'),
                    validationType: VALIDATION_TYPES.MONEY,
                    currentData: { relatedExpense: localState.relatedExpense },
                    fields: []
                }}
                inputActions={{
                    onFieldChange
                }}
            />
        );
    };

    const getDropdownInput = inputsData => {
        const {
            additionalInput: {
                additionalInputText,
                required
            }
        } = inputsData;

        const propName = localState.type === OTHER ? 'subType' : 'profit';
        const options = localState.type === OTHER ? DROPDOWN_OPTIONS.OTHER_INCOME_SUBTYPES : DROPDOWN_OPTIONS.PROFIT_LOSS;

        return (
            <Fragment>
                <UniversalInput
                    label={{
                        text: additionalInputText
                    }}
                    inputData={{
                        isInputRequired: required,
                        type: INPUT_TYPES.DROPDOWN,
                        name: propName,
                        value: localState[propName],
                        returnAsBoolean: true,
                        placeholder: additionalInputText.replace('label', 'placeholder'),
                        options: options,
                        validationType: VALIDATION_TYPES.CHOSEN,
                        currentData: { [propName]: localState[propName] },
                        fields: []
                    }}
                    inputActions={{
                        onFieldChange
                    }}
                />
                {
                    localState.subType !== UNSPECIFIED &&

                        <InstructionalText
                            Component = {
                                <p className = 'usa-grid' data-instructions = { getDataInstructions(localState.subType) } />
                            }
                            styleType = { MODAL_STYLE_TYPE }
                        />
                }
            </Fragment>
        );
    };

    const getRestOfInputs = () => {
        const inputsData = parameter === 'incomes' ?
            INCOME_SOURCES.find(source => source.type === localState.type) :
            DEDUCTIONS_SOURCES.find(source => source.type === localState.type);

        const mockInputsData = {
            isOnlyPositiveAvailable: false,
            additionalInput: {
                positionOnPage: null,
            },
            notice: {
                positionOnPage: null,
                text: ''
            },
            questionText: '',
            frequencyOptions: 'type_0'
        };

        const {
            isOnlyPositiveAvailable,
            additionalInput: {
                positionOnPage: inputPosition
            },
            notice: {
                positionOnPage: noticePosition,
                text: noticeText
            },
            questionText,
            frequencyOptions
        } = inputsData ? inputsData : mockInputsData;

        const getFrequencyOptions = () => {
            return DROPDOWN_OPTIONS
                .FREQUENCY
                .filter(fr => FREQUENCY_OPTIONS_TYPES[frequencyOptions].includes(fr.value));
        };


        return inputsData ?
            <Fragment>
                {
                    noticePosition === 0 &&
                    <div className='grid-container'>
                        <p className="grid-row controls">
                            <FormattedMessage
                                id={noticeText}
                                values={{
                                    personName: normalizeNameOfPerson(name),
                                    coverageYear
                                }}
                                tagName='strong'
                            />
                        </p>
                    </div>
                }

                {
                    inputPosition === 1 &&
                    getAdditionalInputByType(inputsData)
                }

                {
                    noticePosition === 1 && localState.subType === OTHER_INCOME &&
                    <div className='grid-container'>
                        <p className="grid-row controls">
                            <FormattedMessage
                                id={noticeText}
                                values={{
                                    personName: normalizeNameOfPerson(name),
                                    coverageYear
                                }}
                                tagName='strong'
                            />
                        </p>
                    </div>
                }

                <div className='grid-container'>
                    <p className="grid-row controls">
                        <FormattedMessage
                            id={questionText}
                            values={{
                                personName: normalizeNameOfPerson(name),
                                coverageYear
                            }}
                            tagName='strong'
                        />
                    </p>
                </div>

                {
                    noticePosition === 2 &&
                    <div className='grid-container'>
                        <p className="grid-row controls">
                            <FormattedMessage
                                id={noticeText}
                                values={{
                                    personName: normalizeNameOfPerson(name),
                                    coverageYear
                                }}
                                tagName='strong'
                            />
                        </p>
                    </div>
                }

                <UniversalInput
                    label={{
                        text: 'ssap.input.label.amount',
                        labelClasses: ['tablet:grid-col-4']
                    }}
                    inputData={{
                        isInputRequired: true,
                        type: INPUT_TYPES.MONEY_INPUT,
                        name: 'amount',
                        value: localState.amount,
                        inputClasses: ['tablet:grid-col-6'],
                        isOnlyPositiveAvailable: !!isOnlyPositiveAvailable,
                        placeholder: 'ssap.input.placeholder.amount',
                        validationType: VALIDATION_TYPES.MONEY,
                        currentData: { amount: localState.amount },
                        fields: []
                    }}
                    inputActions={{
                        onFieldChange
                    }}
                />

                {
                    inputPosition === 2 &&
                    getAdditionalInputByType(inputsData)
                }

                <UniversalInput
                    label={{
                        text: 'ssap.input.label.how_often',
                        isLabelRequired: false
                    }}
                    inputData={{
                        isInputRequired: true,
                        type: INPUT_TYPES.DROPDOWN,
                        name: 'frequency',
                        value: localState.frequency,
                        options: getFrequencyOptions(),
                        validationType: VALIDATION_TYPES.CHOSEN,
                        currentData: { frequency: localState.frequency },
                        fields: []

                    }}
                    inputActions={{
                        onFieldChange
                    }}
                />

                {
                    (localState.frequency === HOURLY ||
                        localState.frequency === DAILY) &&
                        localState.type === JOB &&

                    <Fragment>
                        <p className="grid-row controls">
                            <FormattedMessage
                                id='modals.income_deduction.how_much_per_week'
                                values={{
                                    personName: normalizeNameOfPerson(name)
                                }}
                                tagName='strong'
                            />
                        </p>
                        <UniversalInput
                            label={{
                                text: localState.frequency === HOURLY ?
                                    'ssap.input.label.hours_per_week' : 'ssap.input.label.days_per_week'
                            }}
                            inputData={{
                                isInputRequired: true,
                                type: INPUT_TYPES.REGULAR_INPUT,
                                name: 'cyclesPerFrequency',
                                value: localState.cyclesPerFrequency,
                                placeholder: 'ssap.input.placeholder.qty',
                                validationType: VALIDATION_TYPES.RANGE,
                                valuesToMatch: localState.frequency === HOURLY ? [1, 168] : [1, 7],
                                validationErrorMessage: localState.frequency === HOURLY ?
                                    VALIDATION_ERROR_MESSAGES.RANGE_UP_TO_168 :
                                    VALIDATION_ERROR_MESSAGES.RANGE_UP_TO_7,
                                currentData: { cyclesPerFrequency: localState.cyclesPerFrequency },
                                fields: []
                            }}
                            inputActions={{
                                onFieldChange
                            }}
                        />
                    </Fragment>
                }

                {
                    noticePosition === 3 &&
                    <div className='grid-container'>
                        <p className="grid-row controls">
                            <FormattedMessage
                                id={noticeText}
                                values={{
                                    personName: normalizeNameOfPerson(name),
                                    coverageYear
                                }}
                                tagName='strong'
                            />
                        </p>
                    </div>
                }

                {
                    inputPosition === 3 &&
                    getAdditionalInputByType(inputsData)
                }

                <UniversalInput
                    legend={{
                        legendText: 'ssap.input.legend.income_expected_for_whole_year',
                        legendTextReplacements: {
                            source: parameter === 'incomes' ?
                                <FormattedMessage id='ssap.input.legend.income_expected_for_whole_year.earn_this_income' /> :
                                <FormattedMessage id='ssap.input.legend.income_expected_for_whole_year.apply_this_deduction' />
                        }
                    }}
                    label={{
                        labelClass: ['tablet:grid-col-4 gi-radio_buttons gi-grid__layout-inline gi-col-layout__legend padding-r-1'],
                        ulClass: ['tablet:grid-col-6 gi-grid__layout-inline'],
                        showRequiredIcon: true
                    }}
                    inputData={{
                        isInputRequired: true,
                        type: INPUT_TYPES.RADIO_BUTTONS,
                        name: 'incomeExpectedForWholeYear',
                        value: localState.incomeExpectedForWholeYear,
                        options: DROPDOWN_OPTIONS.YES_NO,
                        validationType: VALIDATION_TYPES.CHOSEN,
                        currentData: { incomeExpectedForWholeYear: localState.incomeExpectedForWholeYear },
                        fields: []
                    }}
                    inputActions={{
                        onFieldChange
                    }}
                />

                {
                    localState.frequency !== YEARLY &&
                        localState.frequency !== ONCE &&
                        localState.incomeExpectedForWholeYear === false &&

                        <Fragment>
                            <UniversalInput
                                label={{
                                    labelClass: ['tablet:grid-col-4 gi-grid__layout-inline gi-col-layout__label'],
                                    text: 'ssap.input.label.income_start_date',
                                    labelTextReplacements: {
                                        type: parameter === 'incomes' ?
                                            <FormattedMessage id='common.income'/> : <FormattedMessage id='common.deduction'/>
                                    },
                                }}
                                inputData={{
                                    isInputRequired: false,
                                    type: INPUT_TYPES.DATE_PICKER,
                                    inputClasses: ['tablet:grid-col-6'],
                                    name: 'incomeStartDate',
                                    value: localState.incomeStartDate,
                                    specificValidation: {
                                        validationType: VALIDATION_TYPES.DATE_OR_NULL
                                    },
                                    currentData: { incomeStartDate: localState.incomeStartDate },
                                    fields: []
                                }}
                                inputActions={{
                                    onFieldChange
                                }}
                            />

                            <UniversalInput
                                label={{
                                    labelClass: ['tablet:grid-col-4 gi-grid__layout-inline gi-col-layout__label'],
                                    text: 'ssap.input.label.income_end_date',
                                    labelTextReplacements: {
                                        type: parameter === 'incomes' ?
                                            <FormattedMessage id='common.income'/> : <FormattedMessage id='common.deduction'/>
                                    },
                                }}
                                inputData={{
                                    isInputRequired: false,
                                    type: INPUT_TYPES.DATE_PICKER,
                                    inputClasses: ['tablet:grid-col-6'],
                                    name: 'incomeEndDate',
                                    value: localState.incomeEndDate,
                                    valuesToMatch: localState.incomeStartDate,
                                    specificValidation: {
                                        validationType: VALIDATION_TYPES.DATE_OR_NULL_GREATER_THAN_START
                                    },
                                    currentData: { incomeEndDate: localState.incomeEndDate },
                                    fields: []
                                }}
                                inputActions={{
                                    onFieldChange
                                }}
                            />
                            
                            {
                                stateCode !== 'NJ' &&

                                    <InstructionalText
                                        Component = {
                                            <p data-instructions = 'instructional_text.income_proration_estimate' />
                                        }
                                        styleType = { MODAL_STYLE_TYPE }
                                        replacements = {{ exchangeName }}
                                    />
                            }
                        </Fragment>
                }

            </Fragment> :
            null;
    };

    const getOptions = (parameter, option) => {
        switch(parameter) {
            case 'incomes':
                return option ?
                    DROPDOWN_OPTIONS.INCOME_SOURCES_TRIBAL_INCOME : index !== null ?
                        [DROPDOWN_OPTIONS.INCOME_SOURCES.find(option => option.value === type)] :
                        DROPDOWN_OPTIONS.INCOME_SOURCES;
            case 'expenses':
                return index !== null ?
                    [DROPDOWN_OPTIONS.DEDUCTION_SOURCES.find(option => option.value === type)] :
                    DROPDOWN_OPTIONS.DEDUCTION_SOURCES;
        }
    };

    const handleHideClick = () => hideModal();

    modalHeaderProps = {
        ...modalHeaderProps,
        modalHeaderTextData: [
            modalHeaderProps.modalHeaderTextData(parameter === 'incomes'),
            { personName: normalizeNameOfPerson(name) }
        ]
    };

    return (
        <Fragment>
            <div className="gi-modal__header__head">
                <ModalHeader { ...modalHeaderProps } />
            </div>
            <div className="gi-modal__body" id = 'addIncomeOrDeductionModal' >
                <Form { ...{ formName } }>
                    <UniversalInput
                        label={{
                            text: 'ssap.input.label.what_type_of',
                            labelTextReplacements: {
                                type: parameter === 'incomes' ?
                                    <FormattedMessage id='common.income'/> : <FormattedMessage id='common.deduction'/>
                            },
                            isLabelRequired: false,
                        }}
                        inputData={{
                            isInputRequired: true,
                            type: INPUT_TYPES.DROPDOWN,
                            name: 'type',
                            value: localState.type,
                            options: getOptions(parameter, option),
                            validationType: VALIDATION_TYPES.CHOSEN,
                            currentData: { type: localState.type },
                            fields: []

                        }}
                        inputActions={{
                            onFieldChange
                        }}
                    />

                    {
                        (localState.type !== null && localState.type !== OTHER) && parameter === 'incomes' &&

                        <InstructionalText
                            Component = {
                                <p className = 'grid-row' data-instructions = { getDataInstructions(localState.type) } />
                            }
                            onlyInstructions = { localState.type === ALIMONY }
                            fadeEffectDisabled = { localState.type === ALIMONY }
                            styleType = { MODAL_STYLE_TYPE }
                        />
                    }

                    {
                        (localState.type === ALIMONY && parameter === 'expenses') &&

                        <InstructionalText
                            Component = {
                                <p
                                    className = 'grid-row'
                                    data-instructions = {
                                        getDataInstructions(localState.type, 'deductions')
                                    }
                                />
                            }
                            onlyInstructions
                            fadeEffectDisabled
                            styleType = { MODAL_STYLE_TYPE }
                        />
                    }

                    {
                        localState.type && getRestOfInputs()
                    }
                </Form>
            </div>
            <div className="gi-modal__footer">
                <div className='grid-row controls'>
                    <div className='grid-offset-4 txt-left'>
                        <Button
                            className='usa-button usa-button--outline gi-button gi-button__outline'
                            onClick={handleHideClick}
                            inputRef={boundAnchor(anchor_2)}
                            id='anchor'
                        >
                            <FormattedMessage id='button.cancel'/>
                        </Button>
                        <Button
                            className='usa-button gi-button gi-button__primary'
                            onClick={handleSaveClick}
                            inputRef={boundAnchor(localState.type ? anchor_1 : {})}
                            disabled={!localState.type}
                        >
                            <FormattedMessage id='button.save'/>
                        </Button>
                    </div>
                </div>
            </div>

        </Fragment>
    );
};

export default AddIncomeOrDeductionSourceModal;
